import { useEffect, useMemo, useRef, useState } from "react";

import AvatarPicker from "./AvatarPicker";
import { presignGameAvatarUpload } from "../../features/game/gameApi";
import cls from "./PlayerProfileForm.module.scss";

import Male1 from "@/assets/UI/game/m1.webp";
import Male2 from "@/assets/UI/game/m2.webp";
import Male3 from "@/assets/UI/game/m3.webp";
import Male4 from "@/assets/UI/game/m4.webp";
import Male5 from "@/assets/UI/game/m5.webp";
import Male6 from "@/assets/UI/game/m6.webp";
import Female1 from "@/assets/UI/game/f1.webp";
import Female2 from "@/assets/UI/game/f2.webp";
import Female3 from "@/assets/UI/game/f3.webp";
import Female4 from "@/assets/UI/game/f4.webp";
import Female5 from "@/assets/UI/game/f5.webp";
import Female6 from "@/assets/UI/game/f6.webp";

const PRESET_AVATARS = {
  male: [
    { key: "male-1", label: "Vortex", src: Male1 },
    { key: "male-2", label: "Nightfall", src: Male2 },
    { key: "male-3", label: "Obsidian", src: Male3 },
    { key: "male-4", label: "Blaze", src: Male4 },
    { key: "male-5", label: "Eclipse", src: Male5 },
    { key: "male-6", label: "Orion", src: Male6 },
  ],
  female: [
    { key: "female-1", label: "Nyra", src: Female1 },
    { key: "female-2", label: "Liora", src: Female2 },
    { key: "female-3", label: "Zyra", src: Female3 },
    { key: "female-4", label: "Elara", src: Female4 },
    { key: "female-5", label: "Blaze", src: Female5 },
    { key: "female-6", label: "Astra", src: Female6 },
  ],
};

export default function PlayerProfileForm({
  onSubmit,
  loading = false,
  gameId,
  gameAccessToken,
  allowUpload = true,
  onGameAccessExpired,
  submitLabel = "Join Lobby",
}) {
  const avatarPickerRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    displayName: "",
    gender: "male",
    initialCoins: 10,
    image: {
      sourceType: "preset",
      avatarKey: PRESET_AVATARS.male[0].key,
      url: PRESET_AVATARS.male[0].src,
      storageKey: "",
    },
  });

  const presetOptions = useMemo(
    () => PRESET_AVATARS[form.gender] || PRESET_AVATARS.male,
    [form.gender],
  );

  useEffect(() => {
    setForm((prev) => {
      if (prev.image.sourceType === "upload") return prev;
      const stillValid = presetOptions.some(
        (option) => option.key === prev.image.avatarKey,
      );
      if (stillValid) return prev;

      return {
        ...prev,
        image: {
          sourceType: "preset",
          avatarKey: presetOptions[0].key,
          url: presetOptions[0].src,
          storageKey: "",
        },
      };
    });
  }, [presetOptions]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const preparedImage =
        (await avatarPickerRef.current?.prepareForSubmit?.()) || form.image;

      await onSubmit({
        ...form,
        image: preparedImage,
        initialCoins: Number(form.initialCoins || 0),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = loading || submitting;
  const busyLabel =
    form.image.sourceType === "upload" && submitting && !loading
      ? "Uploading avatar..."
      : loading
        ? "Saving..."
        : submitLabel;

  return (
    <form className={cls.form} onSubmit={handleSubmit}>
      <div className={cls.heroCard}>
        <div className={cls.heroText}>
          <p className={cls.eyebrow}>Player Setup</p>
          <h2 className={cls.title}>Create your game identity</h2>
          <p className={cls.subtitle}>
            Choose your name, your style, and the number of layers you’re
            risking in this round 😈
          </p>
        </div>
      </div>

      <div className={cls.formPanel}>
        <div className={cls.grid}>
          <label className={cls.field}>
            <span>Display Name</span>
            <input
              value={form.displayName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  displayName: event.target.value,
                }))
              }
              required
              maxLength={80}
              placeholder="Enter player name"
            />
          </label>

          <label className={cls.field}>
            <span>Gender</span>
            <div className={cls.selectWrap}>
              <select
                value={form.gender}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, gender: event.target.value }))
                }
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </label>

          <label className={cls.field}>
            <span>Starting Layers</span>
            <input
              type="number"
              min="0"
              value={form.initialCoins}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  initialCoins: event.target.value,
                }))
              }
              required
            />
          </label>
        </div>
      </div>

      <div className={cls.avatarPanel}>
        <AvatarPicker
          ref={avatarPickerRef}
          value={form.image}
          allowUpload={allowUpload}
          gameId={gameId}
          gameAccessToken={gameAccessToken}
          presetOptions={presetOptions}
          presignAvatarUpload={presignGameAvatarUpload}
          onChange={(nextImage) =>
            setForm((prev) => ({ ...prev, image: nextImage }))
          }
          onGameAccessExpired={onGameAccessExpired}
        />
      </div>

      <div className={cls.submitRow}>
        <button type="submit" disabled={isBusy} className={cls.submitButton}>
          <span className={cls.submitGlow} />
          <span className={cls.submitText}>{busyLabel}</span>
        </button>
      </div>
    </form>
  );
}
